import * as DialogPrimitive from "@radix-ui/react-dialog";
import { PhoneCall, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { CallDetailContent } from "@/features/calls/pages/CallDetailPage";

type Props = {
  /** Call to show; null keeps the sheet closed. */
  callId: string | null;
  onClose: () => void;
};

/**
 * Wide right-side slide-over showing the full call detail (same content as
 * /calls/{id}) without leaving the assistant's Logs tab. Same pattern as
 * SettingsSheet, just sized for the detail cards.
 */
export default function CallDetailSheet({ callId, onClose }: Props) {
  return (
    <DialogPrimitive.Root open={!!callId} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/40 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-y-0 right-0 z-50 flex w-full max-w-4xl flex-col border-l bg-background shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            "duration-200",
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
            <div className="flex items-center gap-2.5">
              <PhoneCall className="size-4 text-muted-foreground" />
              <div>
                <DialogPrimitive.Title className="text-sm font-semibold">
                  Call Details
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
                  Transcript, summary and recording for this call.
                </DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <X className="size-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {callId && <CallDetailContent id={callId} />}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
